import { Box, Flex, Heading, Text, VStack, HStack } from "@libs/design";
import { Award } from "@assets/icons";

export type Country = {
  country: string;
  code: string;
  score: number;
  missing: number;
  status: string;
  trend: "up" | "down" | "stable";
  source?: string;
  subregion?: string;
  year?: number | null;
  month?: string | null;
};

export type LeaderboardProps = {
  data: Country[];
  embedded?: boolean;
};

const FONT_SIZE = 12;

export const Leaderboard = ({ data, embedded = false }: LeaderboardProps) => {
  const colors = {
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    background: "#F9FAFB",
    progressBg: "#E5E7EB",
    hoverBg: "#F3F4F6",
    border: "#E5E7EB",
    // Rank colors - better and more distinct

    rank: { bg: "#D1FAE5", color: "#059669" },
  };

  const getProgressColor = (score: number) => {
    // Different shades of green based on percentage
    if (score >= 99) return "#059669"; // Dark green for 99%+
    if (score >= 98) return "#10B981"; // Green for 98-98.9%
    if (score >= 97) return "#34D399"; // Light green for 97-97.9%
    if (score >= 96) return "#6EE7B7"; // Lighter green for 96-96.9%
    if (score >= 95) return "#A7F3D0"; // Very light green for 95-95.9%
    if (score >= 94) return "#D1FAE5"; // Lightest green for 94-94.9%
    return "#FEE2E2"; // Light red for <94%
  };

  const inner = (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading fontSize="md" fontWeight="semibold" color="gray.900" mb={1}>
            Leaderboard
          </Heading>
          <Text fontSize="xs" color="gray.500">
            Top performing countries
          </Text>
        </Box>
        <Box
          as="span"
          color="green.500"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          <Award size={20} stroke="currentColor" strokeWidth={2.5} />
        </Box>
      </Flex>
      <VStack gap={3} align="stretch">
        {data.slice(0, 5).map((country, i) => {
          const rankStyle = colors.rank;
          const progressColor = getProgressColor(country.score);

          return (
            <Box
              key={i}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
              _hover={{
                bg: colors.hoverBg,
                borderColor: "#D1D5DB",
              }}
              transition="all 0.2s"
            >
              <HStack gap={3}>
                <Box
                  w={10}
                  h={10}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="sm"
                  fontWeight="medium"
                  style={{
                    backgroundColor: rankStyle.bg,
                    color: rankStyle.color,
                  }}
                >
                  {i + 1}
                </Box>
                <Box flex={1} minW={0}>
                  <Flex justify="space-between" align="center" mb={2.5}>
                    <Text
                      fontWeight="medium"
                      style={{ color: colors.textPrimary, fontSize: FONT_SIZE }}
                    >
                      {country.code}
                    </Text>
                    <Text
                      fontWeight="medium"
                      style={{ color: colors.textPrimary, fontSize: FONT_SIZE }}
                    >
                      {country.score}%
                    </Text>
                  </Flex>
                  <Box
                    h={3}
                    borderRadius="full"
                    overflow="hidden"
                    position="relative"
                    style={{ backgroundColor: colors.progressBg }}
                  >
                    <Box
                      h="100%"
                      borderRadius="full"
                      w={`${country.score}%`}
                      transition="all 0.3s ease"
                      style={{ backgroundColor: progressColor }}
                    />
                  </Box>
                </Box>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );

  if (embedded) return inner;
  return (
    <Box bg="white" borderRadius="xl" borderWidth="1px" boxShadow="sm">
      {inner}
    </Box>
  );
};
